import { Component, ErrorInfo, ReactNode } from 'react';
import ErrorPage from './ErrorPage';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  runtimeErrorId: string | null;
  runtimeMessage: string | null;
}

const isDevelopment = import.meta.env.DEV;

function buildRuntimeErrorId(): string {
  return `runtime-${Date.now().toString(36)}`;
}

export default class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      runtimeErrorId: null,
      runtimeMessage: null
    };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return {
      hasError: true,
      runtimeErrorId: buildRuntimeErrorId(),
      runtimeMessage: error?.message || null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Keep stack-level diagnostics in development while avoiding user-facing leakage in production.
    if (isDevelopment) {
      console.error('Runtime UI error captured by AppErrorBoundary', error, errorInfo);
    }
  }

  private handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const safeMessage = isDevelopment && this.state.runtimeMessage
        ? `A runtime UI error occurred: ${this.state.runtimeMessage}`
        : 'Something went wrong while rendering this page. Please try again.';

      return (
        <ErrorPage
          statusCode={500}
          title="Internal Server Error"
          message={safeMessage}
          requestId={this.state.runtimeErrorId}
          primaryAction={{ label: 'Go Home', to: '/login' }}
          secondaryAction={{ label: 'Retry', onClick: this.handleRetry }}
        />
      );
    }

    return this.props.children;
  }
}
